/**
 * The Station: the between-galaxies hub, laid out like IdleVoid's hub screen.
 *
 *   corner buttons │ orbital │ corner buttons
 *   player title + three-stat card
 *   next-action card · Command Center cards · Active Bonuses chips
 *   ▶ LAUNCH (pinned to the bottom)
 *
 * The Rooms / Ships / Matrix / Crew views (and Challenge, Record, Codex) open
 * in place of the hub from its own bottom nav, corner buttons and cards.
 * Every Fold docks here; it can be opened any time and the galaxy keeps mining.
 */

import { SHIPS } from '../../content/ships';
import { ROOMS } from '../../content/rooms';
import { MATRIX_NODES } from '../../content/matrix';
import { ALL_RESOURCES } from '../../content/resources';
import { ADDITIONS } from '../../content/flags';
import { roomLevel, roomMult, nextRoomCost, roomsBuilt } from '../../sim/rooms';
import { canBuyShip, ownsShip, shipMult } from '../../sim/ships';
import { canReach, nextLevelCost } from '../../sim/actions';
import { canStartChallenge, challengeRun, challengeUnlocked, CHALLENGE_UNLOCK_FOLDS } from '../../sim/challenges';
import { esc, fmtFine, fmtNum, fmtTime, price } from '../format';
import { hullSvg } from '../hulls';
import { icon } from '../icons';
import { isMuted } from '../sound';
import { resGlyph, type StationTab, type View } from '../view';
import { challengeSection, matrixSection } from './fold';
import { crewView, fittingsView, roomsView, shipsView } from './projects';

/** The Station's bottom nav. Challenge, Record and Codex open from the hub instead. */
export const STATION_TABS: readonly { id: StationTab; label: string; icon: string }[] = [
  { id: 'hub', label: 'Hub', icon: icon('hub') },
  { id: 'rooms', label: 'Rooms', icon: icon('layers') },
  { id: 'ships', label: 'Ships', icon: icon('fleet') },
  { id: 'matrix', label: 'Matrix', icon: icon('atom') },
  { id: 'crew', label: 'Crew', icon: icon('users') },
];

export function stationTabs(): readonly { id: StationTab; label: string; icon: string }[] {
  return ADDITIONS.fittings ? [...STATION_TABS, { id: 'fittings', label: 'Fittings', icon: icon('crystal') }] : STATION_TABS;
}

// ── Orbital ────────────────────────────────────────────────────────────────

/** Orbit radii for ships, inner to outer; ships share them round-robin. */
const SHIP_ORBITS = [66, 84, 100] as const;
const ROOM_ORBIT = 44;

/** Fixed star specks on the disc (deterministic, so morph never re-creates them). */
const SPECKS = Array.from({ length: 26 }, (_, i) => {
  const a = i * 2.39996;
  const r = 30 + ((i * 37) % 80);
  return `<circle cx="${(Math.cos(a) * r).toFixed(1)}" cy="${(Math.sin(a) * r).toFixed(1)}" r="${i % 5 === 0 ? 0.9 : 0.5}" fill="#bfe9ff" opacity="${(0.25 + (i % 4) * 0.12).toFixed(2)}"/>`;
}).join('');

const TICKS = Array.from({ length: 48 }, (_, k) => {
  const a = (k / 48) * Math.PI * 2;
  const r0 = k % 4 === 0 ? 105 : 108;
  return `<line x1="${(Math.cos(a) * r0).toFixed(1)}" y1="${(Math.sin(a) * r0).toFixed(1)}" x2="${(Math.cos(a) * 111).toFixed(1)}" y2="${(Math.sin(a) * 111).toFixed(1)}"/>`;
}).join('');

function hex(r: number): string {
  return Array.from({ length: 6 }, (_, k) => {
    const a = (Math.PI / 3) * k - Math.PI / 2;
    return `${(Math.cos(a) * r).toFixed(1)},${(Math.sin(a) * r).toFixed(1)}`;
  }).join(' ');
}

/**
 * IdleVoid's hub orbital: dark disc, rings, a pulsing core. Ours puts the
 * Foundry's hex core in the middle, one module per built Room circling close
 * in, and every owned hull flying its own orbit. Pure SVG + CSS animation.
 */
function orbital(v: View): string {
  const { state } = v;
  const owned = SHIPS.filter((s) => ownsShip(state, s.id));
  const built = ROOMS.filter((r) => roomLevel(state, r.id) > 0);
  const rings = [ROOM_ORBIT, ...SHIP_ORBITS].map((r) =>
    `<circle r="${r}" fill="none" stroke="#3aa8d8" stroke-width="1" opacity="${r === ROOM_ORBIT ? 0.32 : 0.18}"/>`).join('');
  const modules = built.map((r, i) => {
    const a = (i / built.length) * Math.PI * 2;
    const x = Math.cos(a) * ROOM_ORBIT;
    const y = Math.sin(a) * ROOM_ORBIT;
    return `<rect x="${(x - 4).toFixed(1)}" y="${(y - 4).toFixed(1)}" width="8" height="8" rx="1.5" fill="${r.color}" transform="rotate(45 ${x.toFixed(1)} ${y.toFixed(1)})"/>`;
  }).join('');
  // Each hull starts at the top of its orbit, nose along the clockwise path;
  // golden-ratio phases spread any number of them evenly round the disc.
  const ships = owned.map((def, i) => {
    const ring = i % SHIP_ORBITS.length;
    const period = 26 + ring * 12;
    const delay = -((i * 0.381966) % 1) * period;
    const w = def.source.kind === 'store' ? 32 : 26;
    return `<g class="vm-orbit" style="animation-duration:${period}s;animation-delay:${delay.toFixed(2)}s">${hullSvg(def, true, { x: 0, y: -SHIP_ORBITS[ring]!, w })}</g>`;
  }).join('');
  return `<svg class="vm-orbital" viewBox="-120 -120 240 240" aria-label="The Station: ${built.length} rooms, ${owned.length} ships">
      <defs>
        <radialGradient id="stDisc"><stop offset="0%" stop-color="#0d2546"/><stop offset="60%" stop-color="#081a33"/><stop offset="100%" stop-color="#040d1c"/></radialGradient>
        <radialGradient id="stCore"><stop offset="0%" stop-color="#f2fdff"/><stop offset="50%" stop-color="#4dd9f5"/><stop offset="100%" stop-color="#0b5f8a"/></radialGradient>
        <radialGradient id="stHalo"><stop offset="0%" stop-color="#4dd9f5" stop-opacity="0.16"/><stop offset="100%" stop-color="#4dd9f5" stop-opacity="0"/></radialGradient>
      </defs>
      <circle r="114" fill="url(#stDisc)"/>
      <circle r="114" fill="none" stroke="#22ddff" stroke-width="1" opacity="0.3"/>
      <g class="vm-orbit" style="animation-duration:240s" stroke="#22ddff" stroke-width="0.7" opacity="0.18">${TICKS}</g>
      ${SPECKS}
      ${rings}
      <circle r="40" fill="url(#stHalo)"/>
      <g class="vm-orbit" style="animation-duration:60s">${modules}</g>
      ${ships}
      <circle r="27" fill="#020912"/>
      <g class="vm-core-spin"><polygon points="${hex(18)}" fill="url(#stCore)" stroke="#d9f8ff" stroke-width="1"/>
        <polygon points="${hex(9)}" fill="none" stroke="#e9fbff" stroke-width="0.8" opacity="0.7"/></g>
      <circle r="24" fill="none" stroke="#4dd9f5" stroke-width="1.2" class="vm-core-pulse"/>
    </svg>`;
}

function dataAttrs(data: Readonly<Record<string, string>> = {}): string {
  return Object.entries(data).map(([k, val]) => `data-${k}="${esc(val)}"`).join(' ');
}

function corner(label: string, ic: string, color: string, act: string, data: Record<string, string> = {}, pip = false, locked = false): string {
  return `<button class="vm-corner ${color}${locked ? ' locked' : ''}" data-act="${act}" ${dataAttrs(data)}>
    <span class="cb-icon">${ic}</span><span class="cb-label">${label}</span>${pip ? '<span class="pip"></span>' : ''}</button>`;
}

// ── Readouts ───────────────────────────────────────────────────────────────

/** Every permanent multiplier on one stat: Matrix (fleet stats) × Rooms × Ships. */
function permanent(v: View): { label: string; value: number }[] {
  const { state, stats } = v;
  const m = (x: number) => 1 + x;
  return [
    { label: 'Mining', value: m(stats.mine) * roomMult(state, 'mine') * shipMult(state, 'mine') },
    { label: 'Ship speed', value: m(stats.ship) * roomMult(state, 'ship') * shipMult(state, 'ship') },
    { label: 'Cargo', value: m(stats.cargo) * roomMult(state, 'cargo') * shipMult(state, 'cargo') },
    { label: 'Smelting', value: m(stats.smelt) * roomMult(state, 'smelt') * shipMult(state, 'smelt') },
    { label: 'Crafting', value: m(stats.craft) * roomMult(state, 'craft') * shipMult(state, 'craft') },
    { label: 'Sell price', value: m(stats.sell) * shipMult(state, 'sell') },
    { label: 'TE at Fold', value: m(stats.te) * roomMult(state, 'foldTe') * shipMult(state, 'foldTe') },
  ];
}

interface NextAction {
  readonly icon: string;
  readonly kicker: string;
  readonly title: string;
  readonly sub: string;
  readonly meta: string;
  readonly act: string;
  readonly data?: Readonly<Record<string, string>>;
}

/** The one thing worth doing here now (IdleVoid's "Current Priority" card). */
function nextAction(v: View): NextAction {
  const { state } = v;
  const r = v.ui.foldReport;
  const roomReady = ROOMS.find((d) => { const c = nextRoomCost(state, d.id); return c !== null && state.te >= c; });
  if (roomReady) {
    return {
      icon: icon('layers'), kicker: r ? `Your galaxy paid ${fmtNum(r.te)} TE` : 'TE ready',
      title: `${roomLevel(state, roomReady.id) ? 'Upgrade' : 'Build'} ${roomReady.name}`, sub: roomReady.text,
      meta: `${fmtNum(nextRoomCost(state, roomReady.id)!)} TE`, act: 'stationTab', data: { tab: 'rooms' },
    };
  }
  if (MATRIX_NODES.some((n) => canReach(state, n.id) && (nextLevelCost(state, n.id) ?? Infinity) <= state.vs)) {
    return { icon: icon('atom'), kicker: 'Void Shards ready', title: 'Grow the Matrix', sub: 'Permanent bonuses, paid for by Challenges.', meta: `${fmtNum(state.vs)} VS`, act: 'stationTab', data: { tab: 'matrix' } };
  }
  const ship = SHIPS.find((s) => canBuyShip(state, s.id));
  if (ship) {
    return { icon: icon('fleet'), kicker: 'Shipyard', title: `Commission the ${ship.name}`, sub: 'A permanent hull for the whole fleet.', meta: `${fmtNum(state.sc)} SC`, act: 'stationTab', data: { tab: 'ships' } };
  }
  if (canStartChallenge(state)) {
    return { icon: icon('tournament'), kicker: 'Challenge open', title: 'Start a 24-hour Challenge', sub: 'A side galaxy that pays Void Shards for the Matrix.', meta: 'Go', act: 'stationTab', data: { tab: 'challenge' } };
  }
  const next = state.bestDepth + 1;
  return {
    icon: icon('planet'), kicker: 'Next push', title: next > 28 ? 'Fold bigger galaxies' : `Reach planet ${next}`,
    sub: 'Nothing here is waiting. Every galaxy should go a little deeper than the last.', meta: `Best ${state.bestDepth}`, act: 'launch',
  };
}

function commandCards(v: View): string {
  const { state } = v;
  const cheapestRoom = Math.min(...ROOMS.map((d) => nextRoomCost(state, d.id) ?? Infinity));
  const owned = SHIPS.filter((s) => ownsShip(state, s.id)).length;
  const levels = Object.values(state.matrix).reduce((a, b) => a + b, 0);
  const run = challengeRun(state);
  const challenge = run && state.challenge
    ? { value: `${fmtTime(Math.max(0, state.challenge.endsAt - state.clock))} left`, sub: `At ${price('gc', run.galaxyValue)}` }
    : challengeUnlocked(state)
      ? { value: canStartChallenge(state) ? 'Ready' : `In ${fmtTime(Math.max(0, state.challengeReadyAt - state.clock))}`, sub: 'Pays Void Shards' }
      : { value: `${state.folds}/${CHALLENGE_UNLOCK_FOLDS} Folds`, sub: 'Unlocks after Folding' };
  const cards = [
    { ic: icon('layers'), name: 'Rooms', value: `${roomsBuilt(state)}/${ROOMS.length} built`, sub: state.te >= cheapestRoom ? 'Upgrade ready' : `Need ${fmtNum(Math.max(0, cheapestRoom - state.te))} TE`, tab: 'rooms', locked: false },
    { ic: icon('fleet'), name: 'Ships', value: `${owned}/${SHIPS.length} hulls`, sub: SHIPS.some((s) => canBuyShip(state, s.id)) ? 'Hull affordable' : 'Earned, SC or store', tab: 'ships', locked: false },
    { ic: icon('atom'), name: 'Matrix', value: `${levels} levels`, sub: `${fmtNum(state.vs)} VS to spend`, tab: 'matrix', locked: false },
    { ic: icon('users'), name: 'Crew', value: `${Object.keys(state.prospectors).length} prospectors`, sub: `${fmtNum(state.sc)} SC`, tab: 'crew', locked: false },
    { ic: icon('tournament'), name: 'Challenge', value: challenge.value, sub: challenge.sub, tab: 'challenge', locked: !challengeUnlocked(state) },
    { ic: icon('record'), name: 'Record', value: `Best planet ${state.bestDepth}`, sub: `${state.folds} Folds`, tab: 'record', locked: false },
  ];
  return cards.map((c) => `<button class="vm-cmd${c.locked ? ' locked' : ''}" data-act="stationTab" data-tab="${c.tab}">
      <span class="vm-cmd-icon">${c.ic}</span>
      <span class="vm-cmd-main"><span class="vm-cmd-name">${c.name}</span><span class="vm-cmd-value">${c.value}</span></span>
      <span class="vm-cmd-sub">${c.sub}</span></button>`).join('');
}

function hubView(v: View): string {
  const { state } = v;
  const r = v.ui.foldReport;
  const perm = permanent(v);
  const n = nextAction(v);
  const chips = perm.filter((p) => p.value > 1.0005).map((p) => `<span class="vm-bonus-chip">${p.label} ×${fmtFine(p.value)}</span>`).join('');
  const sub = r ? `Galaxy sold · ${price('gc', r.value)} · planet ${r.depth}` : 'Docked · your galaxy is still mining';
  return `<div class="vm-orbital-region" data-key="orbital">
      <div class="vm-corner-col">
        ${corner('Challenge', icon('tournament'), 'c-gold', 'stationTab', { tab: 'challenge' }, canStartChallenge(state), !challengeUnlocked(state))}
        ${corner('Record', icon('record'), 'c-teal', 'stationTab', { tab: 'record' })}
        ${corner('Codex', icon('vault'), 'c-purple', 'stationTab', { tab: 'codex' })}
      </div>
      <div class="vm-orbital-col">${orbital(v)}</div>
      <div class="vm-corner-col">
        ${corner('Store', icon('store'), 'c-gold', 'stationTab', { tab: 'ships' })}
        ${corner(isMuted() ? 'Muted' : 'Sound', icon(isMuted() ? 'muted' : 'speaker'), 'c-blue', 'mute')}
        ${corner('Galaxy', icon('planet'), 'c-green', 'launch')}
      </div>
    </div>
    <div class="vm-hub-info" data-key="info">
      <div class="vm-hub-title">Foundry Commander</div>
      <div class="vm-hub-sub">${sub}</div>
      <div class="vm-hub-stats">
        <div class="vm-hub-stat"><div class="l">Best depth</div><div class="v">${state.bestDepth || '—'}</div><div class="s">deepest planet</div></div>
        <div class="vm-hub-stat"><div class="l">Mining</div><div class="v">×${fmtFine(perm[0]!.value)}</div><div class="s">all permanent</div></div>
        <div class="vm-hub-stat"><div class="l">TE bonus</div><div class="v">×${fmtFine(perm[6]!.value)}</div><div class="s">per Fold</div></div>
      </div>
    </div>
    <button class="vm-next" data-key="next" data-act="${n.act}" ${dataAttrs(n.data)}>
      <span class="vm-next-icon">${n.icon}</span>
      <span class="vm-next-copy"><span class="vm-next-kicker">${n.kicker}</span><span class="vm-next-title">${esc(n.title)}</span><span class="vm-next-sub">${esc(n.sub)}</span></span>
      <span class="vm-next-meta">${n.meta}</span>
    </button>
    <div class="vm-cmd-wrap" data-key="cmd"><div class="vm-cmd-label">Command Center</div><div class="vm-cmd-cards">${commandCards(v)}</div></div>
    <div class="vm-bonuses" data-key="bonuses"><div class="vm-bonuses-label">Active bonuses</div>
      <div class="vm-bonuses-chips">${chips || '<span class="vm-note vm-faint">None yet: build Rooms, earn ships, grow the Matrix.</span>'}</div></div>`;
}

function recordView(v: View): string {
  const { state } = v;
  const rows: [string, string][] = [
    ['Deepest planet', `${state.bestDepth}`],
    ['Folds', `${state.folds}`],
    ['Lifetime GC', price('gc', state.lifetime.gc)],
    ['Items crafted', fmtNum(state.lifetime.crafted)],
    ['Challenges', `${state.challengesDone}${state.challengesDone ? ` · best ${price('gc', state.bestChallenge)}` : ''}`],
    ['Rooms built', `${roomsBuilt(state)}/${ROOMS.length}`],
    ['Ships', `${SHIPS.filter((s) => ownsShip(state, s.id)).length}/${SHIPS.length}`],
    ['Matrix levels', `${Object.values(state.matrix).reduce((a, b) => a + b, 0)}`],
  ];
  const perm = permanent(v).map((p) => `<dt>${p.label}</dt><dd>×${fmtFine(p.value)}</dd>`).join('');
  return `<div class="vm-section-head cyan">Record</div>
    <dl class="vm-kv">${rows.map(([k, val]) => `<dt>${k}</dt><dd>${val}</dd>`).join('')}</dl>
    <div class="vm-section-head cyan" style="margin-top:12px">Permanent multipliers <span class="aside">Matrix × Rooms × Ships</span></div>
    <dl class="vm-kv">${perm}</dl>`;
}

function codexView(v: View): string {
  const found = new Set(v.state.discovered);
  const group = (kind: 'ore' | 'alloy' | 'item', title: string) => {
    const defs = ALL_RESOURCES.filter((d) => d.kind === kind);
    const n = defs.filter((d) => found.has(d.id)).length;
    const cells = defs.map((d) => found.has(d.id)
      ? `<div class="vm-codex-cell">${resGlyph(d.id)}<span>${esc(d.name)}</span></div>`
      : `<div class="vm-codex-cell unknown">${icon('lock')}<span>Undiscovered</span></div>`).join('');
    return `<div class="vm-subhead">${title} · ${n}/${defs.length}</div><div class="vm-codex">${cells}</div>`;
  };
  return `<div class="vm-section-head cyan">Codex <span class="aside">${found.size}/${ALL_RESOURCES.length} discovered</span></div>
    ${group('ore', 'Ores')}${group('alloy', 'Bars')}${group('item', 'Items')}`;
}

const VIEWS: Readonly<Record<StationTab, (v: View) => string>> = {
  hub: hubView,
  rooms: roomsView,
  ships: shipsView,
  matrix: matrixSection,
  crew: crewView,
  challenge: challengeSection,
  record: recordView,
  codex: codexView,
  fittings: fittingsView,
};

function launchFooter(v: View): string {
  const fresh = !!v.ui.foldReport;
  return `<div class="vm-launch" data-key="launch">
    <button class="vm-launch-btn" data-act="launch">▶ ${fresh ? 'Launch new galaxy' : 'Back to galaxy'}</button>
    <div class="vm-launch-sub">★ ${fresh ? 'Ready to launch' : 'Your galaxy is still mining'}</div>
  </div>`;
}

export function renderStation(v: View): string {
  const tab = v.ui.stationTab;
  const body = VIEWS[tab](v);
  // The hub has its own layout; Matrix and Challenge render their own sections.
  const wrapped = tab === 'hub' || tab === 'matrix' || tab === 'challenge' ? body : `<section class="vm-section" data-key="st-${tab}">${body}</section>`;
  return `<div class="vm-station" data-key="station-${tab}">${wrapped}</div>${launchFooter(v)}`;
}

/** Hub header: game label left, currency tiles right (IdleVoid's hub header). */
export function renderStationHead(v: View): string {
  const tile = (c: 'te' | 'sc' | 'vs', label: string, n: number) =>
    `<div class="vm-cur-tile ${c}"><span class="ct-label">${label}</span><span class="ct-val">${fmtNum(n)}</span></div>`;
  return `<div class="vm-st-head">
      <div class="vm-st-head-left"><div class="vm-st-game">VoidMiner</div><div class="vm-st-label">Station</div></div>
      <div class="vm-cur-tiles">${tile('te', '◇ TE', v.state.te)}${tile('sc', '★ SC', v.state.sc)}${tile('vs', '✦ VS', v.state.vs)}</div>
    </div>`;
}
